/*
  Event guide: https://support.google.com/analytics/answer/1033068#Anatomy
  Submitting events: https://developers.google.com/analytics/devguides/collection/gtagjs/events

  action: the doing part of the event "click", "play", "pause" or something to describe target of the event
  label: a specific identifier for the event, e.g. which nav item, or what video, what file, etc etc
  category: The group of events that is taking place.

  Note that it's not nessicary to await this function unles you specifically want to wait for the event to
  finish sending. The event will always be sent before the page unloads.
*/
async function trackGenericEvent(unParsedAction, unParsedCategory, unParsedLabel) {
  if (!window.gtag) {
    throw new Error("Gtag isn't loaded - make sure it's loaded before tracking events")
  }

  // we parse the tracking data so that it conforms to standard - all lower case
  const [action, category, label] = [unParsedAction, unParsedCategory, unParsedLabel].map(str => {
    if (str) {
      return str.trim().toLowerCase()
    }
  })

  return new Promise(resolve => {
    // if the envionment is dev DONT send data to google analytics. This is so that we don't pollute
    // our tracking with our testing or broken data while we work on a feature. Instead, just print to the
    // console
    if (PetRescue.env.environment === 'development') {
      console.info('Tracking event triggered', { category, label, action })
      return resolve()
    }

    // set a timeout to resolve in the event gtag fails to send the event. This way awaited events don't disrupt
    // the critical path.
    setTimeout(resolve, 500)

    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      event_callback: resolve,
    })
  })
}

export function createAnalyticsGroup(category) {
  return (action, label) => trackGenericEvent(action, category, label)
}

/*
  Purchasing tracking helpers
*/

// https://developers.google.com/analytics/devguides/collection/gtagjs/ecommerce
export function trackPurchase({ transaction_id, affiliation, currency, value, frequency }) {
  const gaPayload = { transaction_id, affiliation, currency, value }
  const fbPayload = { value, currency, frequency }

  // Don't trigger in dev so we don't pollute our analytics
  if (PetRescue.env.environment === 'development') {
    console.info('Payment tracking event triggered', gaPayload)
    return
  }

  window.gtag('event', 'purchase', gaPayload)
  if (window.fbq) window.fbq('track', 'Donate', fbPayload)
}

// https://support.google.com/google-ads/answer/6331314
export function trackConversion({ transaction_id, account_id = 'AW-363067286' }) {
  const gaPayload = { send_to: `${account_id}/B7WuCOXs1csCEJbvj60B`, transaction_id }

  // Don't trigger in dev or staging so we don't pollute our analytics
  if (PetRescue.env.environment !== 'production') {
    console.info('Conversion tracking event triggered', gaPayload)
    return
  }

  window.gtag('event', 'conversion', gaPayload)
}

// Inline tracking for use in articles etc...
// Usage example: <a href="https://example.com" class="js-track-click" data-category="monthly-report">Link to page from monthly report article</a>

[].forEach.call(document.querySelectorAll('.js-track-click'), function(el) {

  const track = createAnalyticsGroup(el.getAttribute('data-category'))

  el.addEventListener('click', function() {

    track('click', el.innerHTML)

  })
})
